/* eslint-disable @typescript-eslint/no-explicit-any */
import { useJsApiLoader } from '@react-google-maps/api'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	type HeadersArgs,
	data,
	useLoaderData,
} from 'react-router'
import MapShowBtn from '~/components/map/map-show-btn'
import Map from '~/components/map/map'
import { getIndexEvents } from '~/queries/indexEvents/getIndexEvents.server'
import { getUserId } from '~/utils/auth/session.server'
import { getServerLang } from '~/utils/i18n.utils'
import { getUserLocation } from '~/utils/location/location.server'
import { capitalizeText, cn } from '~/utils/misc'
import { getUrlQueries } from '~/utils/queries/getUrlQueries.server'
import { SetMeta } from '~/utils/seo/seo'
import { makeTimings, time } from '~/utils/timing.server'
import i18next from '~/configs/i18next.server'
import { useNonce } from '~/utils/nonce-provider'

const libraries: ['places'] = ['places']

function MapWrapper({
	events,
	userLocation,
}: {
	events: any[]
	userLocation: any
}) {
	const nonce = useNonce()
	const { isLoaded, loadError } = useJsApiLoader({
		googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY ?? '',
		language: 'sv',
		libraries,
		nonce,
	})

	if (loadError) {
		throw new Error(loadError.message)
	}

	if (!isLoaded) {
		return (
			<div className="flex h-full w-full items-center justify-center">
				<div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
			</div>
		)
	}

	return <Map events={events} userLocation={userLocation} isLoaded={isLoaded} />
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const timings = makeTimings('map loader')
	const userLocation = await getUserLocation(request, params)
	const {
		countryCode,
		city,
		swLat,
		swLng,
		neLat,
		neLng,
		latitude,
		longitude,
		havePosition,
		nearbySelected,
		radius,
	} = userLocation
	const { fromDate, toDate, categories, searchText } =
		await getUrlQueries(request)
	const lng = getServerLang(request)
	const t = await i18next.getFixedT(lng.lngShort, 'common')
	const userId = await getUserId(request)
	const events = await time(
		async () =>
			await getIndexEvents({
				fromDate: fromDate,
				toDate: toDate,
				countryCode: countryCode,
				city: city,
				radius: havePosition && nearbySelected ? radius : undefined,
				latitude: havePosition && nearbySelected ? latitude : undefined,
				longitude: havePosition && nearbySelected ? longitude : undefined,
				swLat: swLat,
				swLng: swLng,
				neLat: neLat,
				neLng: neLng,
				categories: categories,
				userId: userId ?? undefined,
				take: 100,
				searchText: searchText,
				lng: lng,
			}),
		{ timings, type: 'get events' },
	)

	return data(
		{
			events,
			userLocation,
			lng,
			title: capitalizeText(t('public_map_area')),
			description: t('public_evently_slogan_description'),
		},
		{ headers: { 'Server-Timing': timings.toString() } },
	)
}

export function headers({ parentHeaders, loaderHeaders }: HeadersArgs) {
	parentHeaders.append('Server-Timing', loaderHeaders.get('Server-Timing')!)
	return parentHeaders
}

export default function Index() {
	const { events, userLocation } = useLoaderData<typeof loader>()

	return (
		<>
			<div className={cn('z-10 h-full w-full')}>
				<MapWrapper events={events} userLocation={userLocation} />
			</div>
			<MapShowBtn toMap={false} />
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ location, data }) => {
	return SetMeta({
		title: data?.title,
		description: data?.description,
		robots: 'noindex',
		location,
	})
}
